import { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/react-hooks'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import VoteIcon from '~/components/shared/Vote/Icon'
import { CreateVote, UpdateVote, DeleteVote } from '~/graphql/Votes.gql'
import { getCurrentUser, isLoggedIn } from '~/utils/auth'
import styles from './index.module.scss'
import CommentModal from '../CommentModal'
import useError from '~/hooks/useError'
import withLocale from '~/hocs/locale'

const Vote = ({
  item, onAuthRequire, onMessageLoad, modal, className, votesLabel
}) => {
  const handleOnComplete = (data) => {
    const {
      positive, negative, userVotes, messages, messagesByPostId
    } = data.vote.agendaItem ?? data.vote.email ?? data.vote.message
    const nextMessages = messages ?? messagesByPostId
    const nextResult = positive?.totalCount - negative?.totalCount

    setResult(nextResult)
    if (onMessageLoad && nextMessages) {
      onMessageLoad(nextMessages.nodes)
    }
    setUserVote(userVotes?.nodes[0])
  }

  const [isCommentModalOpen, setCommentModalOpen] = useState(false)
  const modalLabel = (
    <>
      <b>{modal.question}</b>{' '}{item.type}
      <span className="text-muted">{' "'}{item.title}{'"'}</span>?
    </>
  )

  const handleOnError = error => {
    dispatchError(error)
  }

  const setReason = ({ reason, asAnonymous, reference }) => ({
    create: {
      body: reason,
      showAuthor: !asAnonymous,
      authorId: user.id,
      ...reference,
    }
  })

  const handleVoteCreate = async (positive, reason, asAnonymous) => {
    const variables = {
      positive,
      userId: user.id,
      ...item.reference,
    }
    if (reason) {
      const reference = item.reasonReference ?? item.reference
      variables.reason = setReason({ reason, asAnonymous, reference })
    }

    await createVote({ variables })
  }

  const handleVoteUpdate = async (voteId, positive, reason, asAnonymous) => {
    const variables = {
      id: voteId,
      positive,
      userId: user.id,
      ...item.reference,
    }
    if (reason) {
      const reference = item.reasonReference ?? item.reference
      variables.reason = setReason({ reason, asAnonymous, reference })
    }

    await updateVote({ variables })
  }

  const handleVoteDelete = async (id) => {
    const variables = {
      id,
      userId: user.id,
    }

    await deleteVote({ variables })
  }

  const handleVoteClick = (positive) => {
    if (!isLoggedIn()) {
      return onAuthRequire()
    }

    if (!positive && (!userVote || userVote.positive)) {
      return setCommentModalOpen(true)
    }

    if (!userVote) {
      return handleVoteCreate(positive)
    }

    const voteId = userVote.id
    if (userVote.positive === positive) {
      return handleVoteDelete(voteId)
    }

    return handleVoteUpdate(voteId, positive)
  }

  const handleOnComment = ({ body, asAnonymous }) => {
    if (!userVote) {
      return handleVoteCreate(false, body, asAnonymous)
    }
    const voteId = userVote.id
    return handleVoteUpdate(voteId, false, body, asAnonymous)
  }

  const { dispatchError } = useError()
  const [result, setResult] = useState(item.positive.totalCount - item.negative.totalCount)
  const [userVote, setUserVote] = useState(item.userVotes?.nodes?.[0])
  const options = {
    onError: handleOnError,
    onCompleted: handleOnComplete,
  }

  const [createVote, { loading: creating }] = useMutation(CreateVote, options)
  const [updateVote, { loading: updating }] = useMutation(UpdateVote, options)
  const [deleteVote, { loading: deleting }] = useMutation(DeleteVote, options)

  const user = getCurrentUser()
  const disabled = creating || updating || deleting

  return (
    <div className={`d-flex flex-md-column align-items-center ${className}`}>
      <VoteIcon
        positive
        onClick={handleVoteClick}
        modifier={userVote?.positive === true ? 'active-up' : ''}
        disabled={disabled}
      />
      <div className={styles.counter}>
        {result}
        <span className="sr-only">&nbsp;{votesLabel}</span>
      </div>
      <VoteIcon
        positive={false}
        onClick={handleVoteClick}
        modifier={userVote?.positive === false ? 'active-down' : ''}
        disabled={disabled}
      />
      <CommentModal
        user={user}
        isOpen={isCommentModalOpen}
        onHide={() => setCommentModalOpen(false)}
        onComment={handleOnComment}
        label={modalLabel}
        title={modal.title}
        placeholder={modal.placeholder}
        icon={faChevronDown}
      />
    </div>
  )
}

Vote.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    positive: PropTypes.shape({
      totalCount: PropTypes.number.isRequired,
    }).isRequired,
    negative: PropTypes.shape({
      totalCount: PropTypes.number.isRequired,
    }).isRequired,
    reference: PropTypes.oneOfType([
      PropTypes.shape({
        agendaItemId: PropTypes.number.isRequired,
      }),
      PropTypes.shape({
        emailId: PropTypes.number.isRequired,
      }),
      PropTypes.shape({
        messageId: PropTypes.number.isRequired,
      }),
    ]).isRequired,
    reasonReference: PropTypes.oneOfType([
      PropTypes.shape({
        postId: PropTypes.number.isRequired,
      }),
    ]),
    userVotes: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          positive: PropTypes.bool.isRequired,
          userId: PropTypes.number.isRequired,
        })
      ),
    }),
  }),
  modal: PropTypes.shape({
    question: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
  }).isRequired,
  onAuthRequire: PropTypes.func.isRequired,
  onMessageLoad: PropTypes.func,
  className: PropTypes.string
}

Vote.defaultProps = {
  item: {
    userVotes: {
      nodes: []
    },
    reasonReference: null,
  },
  onMessageLoad: undefined,
  className: null
}

export default withLocale('common', 'vote')(Vote)
